/**
 * Global Search Hook - Modular Architecture
 * Main hook that orchestrates parallel searches across multiple tables
 * 
 * Features:
 * - Parallel search execution with Promise.allSettled
 * - Debounced search term
 * - AbortController for canceling outdated searches
 * - Relevance-based sorting
 * - Lookup data caching (sparten, gesellschaften)
 * - Client-side filtering (kundentyp, leadheldenLead)
 */

import { useQuery } from '@tanstack/react-query';
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { SearchResult, UseGlobalSearchOptions, SearchSectionId } from './types';
import { sortByRelevance } from './utils';
import { useLookupData } from './useLookupData';
import {
  searchKunden,
  searchEigenvertraege,
  searchFremdvertraege,
  searchGesellschaften,
  searchAnsprechpartner,
  searchLeads,
  searchAufgaben,
  searchVertriebspartner,
  searchAbrechnungen,
  searchZugaenge,
  searchVermittlernummern,
  searchRabatte,
  searchDownloadcenter,
  searchBuchhaltung,
  searchElearning,
  searchVorlagen,
  searchBerichte,
  searchEinstellungen,
} from './searchFunctions';

export function useGlobalSearch(searchTerm: string, options: UseGlobalSearchOptions) {
  const { filters, limit = 10 } = options;
  const [debouncedTerm, setDebouncedTerm] = useState(searchTerm);
  
  // AbortController for canceling outdated searches
  const abortControllerRef = useRef<AbortController | null>(null);

  // Lookup data caching (Phase 2.1)
  const { prefetch: prefetchLookupData, isLoading: lookupLoading } = useLookupData();

  // Prefetch lookup data on mount
  useEffect(() => {
    prefetchLookupData();
  }, [prefetchLookupData]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Cancel previous search when starting a new one
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [debouncedTerm]);

  // Callback to trigger prefetch (can be called on search input focus)
  const onSearchFocus = useCallback(() => {
    prefetchLookupData();
  }, [prefetchLookupData]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['globalSearch', debouncedTerm, Array.from(filters.enabledSections), filters.kundentyp, filters.leadheldenLead, filters.exactMatch, filters.fuzzyMatch],
    queryFn: async () => {
      // Cancel any previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      const term = debouncedTerm.trim();
      if (term.length < 2) return [];

      const enabledSections = filters.enabledSections;
      const exactMatch = filters.exactMatch || false;
      const fuzzyMatch = filters.fuzzyMatch || false;
      const searchPromises: Promise<SearchResult[]>[] = [];

      // Add search promises based on enabled sections
      if (enabledSections.has('kunden')) {
        searchPromises.push(searchKunden(term, limit, exactMatch, fuzzyMatch, signal));
      }
      if (enabledSections.has('eigenvertraege')) {
        searchPromises.push(searchEigenvertraege(term, limit, exactMatch, fuzzyMatch, signal));
      }
      if (enabledSections.has('fremdvertraege')) {
        searchPromises.push(searchFremdvertraege(term, limit, exactMatch, fuzzyMatch, signal));
      }
      if (enabledSections.has('gesellschaften')) {
        searchPromises.push(searchGesellschaften(term, limit, exactMatch, fuzzyMatch, signal));
      }
      if (enabledSections.has('ansprechpartner')) {
        searchPromises.push(searchAnsprechpartner(term, limit, exactMatch, fuzzyMatch, signal));
      }
      if (enabledSections.has('leads')) {
        searchPromises.push(searchLeads(term, limit, exactMatch, fuzzyMatch, signal));
      }
      if (enabledSections.has('aufgaben')) {
        searchPromises.push(searchAufgaben(term, limit, exactMatch, fuzzyMatch, signal));
      }
      if (enabledSections.has('vertriebspartner')) {
        searchPromises.push(searchVertriebspartner(term, limit, exactMatch, fuzzyMatch, signal));
      }
      if (enabledSections.has('abrechnungen')) {
        searchPromises.push(searchAbrechnungen(term, limit, exactMatch, fuzzyMatch, signal));
      }
      if (enabledSections.has('zugaenge')) {
        searchPromises.push(searchZugaenge(term, limit, exactMatch, fuzzyMatch, signal));
      }
      if (enabledSections.has('vermittlernummern')) {
        searchPromises.push(searchVermittlernummern(term, limit, exactMatch, fuzzyMatch, signal));
      }
      if (enabledSections.has('rabatte')) {
        searchPromises.push(searchRabatte(term, limit, exactMatch, fuzzyMatch, signal));
      }
      if (enabledSections.has('downloadcenter')) {
        searchPromises.push(searchDownloadcenter(term, limit, exactMatch, fuzzyMatch, signal));
      }
      if (enabledSections.has('buchhaltung')) {
        searchPromises.push(searchBuchhaltung(term, limit, exactMatch, fuzzyMatch, signal));
      }
      if (enabledSections.has('elearning')) {
        searchPromises.push(searchElearning(term, limit, exactMatch, fuzzyMatch, signal));
      }
      if (enabledSections.has('vorlagen')) {
        searchPromises.push(searchVorlagen(term, limit, exactMatch, fuzzyMatch, signal));
      }
      if (enabledSections.has('berichte')) {
        searchPromises.push(searchBerichte(term, limit, exactMatch, fuzzyMatch, signal));
      }
      if (enabledSections.has('einstellungen')) {
        searchPromises.push(searchEinstellungen(term, limit, exactMatch, fuzzyMatch, signal));
      }

      // Execute all searches in parallel
      const results = await Promise.allSettled(searchPromises);
      
      // Check if request was aborted
      if (signal.aborted) {
        return [];
      }
      
      // Flatten results
      let allResults: SearchResult[] = [];
      results.forEach(result => {
        if (result.status === 'fulfilled') {
          allResults = allResults.concat(result.value);
        }
      });

      // Sort by relevance score
      return sortByRelevance(allResults);
    },
    enabled: debouncedTerm.trim().length >= 2,
    staleTime: 30000,
  });

  // Apply additional filters
  const filteredResults = useMemo(() => {
    let results = data || [];

    // Filter by kundentyp
    if (filters.kundentyp && filters.kundentyp !== 'all') {
      results = results.filter(r => {
        if (r.type === 'kunden') {
          return r.data.kundentyp === filters.kundentyp;
        }
        return true;
      });
    }

    // Filter by leadheldenLead
    if (filters.leadheldenLead && filters.leadheldenLead !== 'all') {
      results = results.filter(r => {
        if (r.type === 'eigenvertraege') {
          const isLeadhelden = r.data.leadhelden_lead === true;
          return filters.leadheldenLead === 'ja' ? isLeadhelden : !isLeadhelden;
        }
        return true;
      });
    }

    return results;
  }, [data, filters.kundentyp, filters.leadheldenLead]);

  // Group results by type
  const groupedResults = useMemo(() => {
    const grouped: Record<SearchSectionId, SearchResult[]> = {} as any;
    filteredResults.forEach(result => {
      if (!grouped[result.type]) {
        grouped[result.type] = [];
      }
      grouped[result.type].push(result);
    });
    return grouped;
  }, [filteredResults]);

  return {
    results: filteredResults,
    groupedResults,
    isLoading: isLoading || lookupLoading,
    error,
    totalCount: filteredResults.length,
    onSearchFocus, // Expose for prefetching on input focus
  };
}
